import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { Box, Snackbar, makeStyles } from "@material-ui/core";
import MuiAlert from "@material-ui/lab/Alert";
import { debounce } from "lodash";
import DOMPurify from "dompurify";

import EditorToolbar from "./EditorToolbar";
import TemplateSelector from "./TemplateSelector";
import DocumentPage from "./DocumentPage";
import {
  applyFormatting,
  applyFormattingAlternative,
  highlightKeywordsInHtml,
} from "./utils";

// Alert component for notifications
const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const useStyles = makeStyles((theme) => ({
  "@global": {
    ".keyword-highlight": {
      backgroundColor: "#ffee58",
      padding: "0px 2px",
      borderRadius: "2px",
      display: "inline",
      boxShadow: "0 0 2px rgba(0,0,0,0.3)",
      fontWeight: "bold",
    },
  },
  root: {
    width: "100%",
  },
  pagesContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center", // Center the pages horizontally
    width: "100%",
    backgroundColor: "#f5f5f5",
    padding: theme.spacing(3),
    // Don't constrain the container height - let it expand with content
    position: "relative",
    overflow: "hidden", // Prevent horizontal scrolling
    [theme.breakpoints.down(1416)]: {
      padding: theme.spacing(2),
    },
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1),
      backgroundColor: "#f0f0f0", // Slightly darker background for better contrast on mobile
      // Add bottom padding to account for mobile controls
      paddingBottom: theme.spacing(8),
    },
  },
  feedbackIndicator: {
    position: "absolute",
    top: theme.spacing(1),
    right: theme.spacing(1),
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: "rgba(0, 0, 0, 0.05)",
    color: theme.palette.text.secondary,
    fontSize: "0.75rem",
    transition: "opacity 0.3s ease-in-out",
    opacity: 0,
    pointerEvents: "none",
    zIndex: 1000,
    [theme.breakpoints.down("sm")]: {
      top: "auto",
      bottom: theme.spacing(7), // Position above mobile controls
      left: "50%",
      transform: "translateX(-50%)",
      backgroundColor: "rgba(33, 150, 243, 0.9)",
      color: "#fff",
      fontWeight: "bold",
      boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
      padding: theme.spacing(0.75, 1.5),
      borderRadius: "20px",
    },
  },
  feedbackVisible: {
    opacity: 1,
  },
  // Add a mobile-specific style for the editor container
  mobileEditorContainer: {
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      // Add styles to improve touch interaction
      "& [contenteditable=true]": {
        WebkitTapHighlightColor: "rgba(68, 138, 255, 0.1)",
        // Improve touch selection handles
        "&::selection": {
          backgroundColor: "rgba(33, 150, 243, 0.3)",
        },
      },
    },
  },
  keywordHighlight: {
    backgroundColor: "#ffee58",
    padding: "0px 2px",
    borderRadius: "2px",
    display: "inline",
    boxShadow: "0 0 2px rgba(0,0,0,0.3)",
    fontWeight: "bold",
  },
}));

/**
 * EnhancedHtmlEditor Component
 *
 * An improved WYSIWYG editor for resumes that works directly with HTML
 *
 * @param {Object} props - Component props
 * @param {Object} props.resumeData - The structured resume data
 * @param {Array} props.templates - Available templates
 * @param {string} props.initialTemplate - Initial template to use
 * @param {Array} props.keywords - Keywords to highlight in the resume
 * @param {Function} props.onContentChange - Callback when content changes
 * @param {Function} props.onDownload - Callback for downloading the document
 * @param {string} props.documentType - Type of document being edited ('resume' or 'coverLetter')
 * @returns {JSX.Element} The rendered component
 */
const EnhancedHtmlEditor = ({
  resumeData,
  templates = [],
  initialTemplate = "standard",
  html: initialHtml = "",
  keywords = [],
  onContentChange,
  onDownload,
  onScrollToATS,
  documentType = "resume", // Default to resume
}) => {
  const classes = useStyles();
  const [html, setHtml] = useState(initialHtml); // This is the editor's current HTML content
  const [downloading, setDownloading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(initialTemplate);
  const [keywordHighlightEnabled, setKeywordHighlightEnabled] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // History stack for undo/redo functionality with improved management
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [isUndoRedo, setIsUndoRedo] = useState(false);

  // Track if content has been modified since last save
  const [isModified, setIsModified] = useState(false);

  // State for selections with improved tracking
  const [selectionRange, setSelectionRange] = useState(null);
  const [colorSelectionRange, setColorSelectionRange] = useState(null);

  // State for operation feedback
  const [operationFeedback, setOperationFeedback] = useState({
    visible: false,
    message: "",
  });

  // Refs for DOM elements
  const editorRef = useRef(null);
  const containerRef = useRef(null);

  // Ref to track if initial render has completed
  const initialRenderRef = useRef(true);

  // Ref to store the previous HTML for comparison
  const prevHtmlRef = useRef("");

  // Ref to track if we're currently updating content to prevent cursor jumps
  const isUpdatingContentRef = useRef(false);

  // Ref to track if we're in the middle of a manual edit
  const isManualEditRef = useRef(false);

  // Effect to ensure selectedTemplate is valid based on available templates
  useEffect(() => {
    // Ensure templates is an array
    if (!Array.isArray(templates)) {
      if (selectedTemplate !== "") {
        setSelectedTemplate("");
      }
      return;
    }

    const isValidTemplateSelected = templates.some(
      (t) => t.id === selectedTemplate
    );

    if (templates.length > 0) {
      // If templates are available but the current selection is invalid
      if (!isValidTemplateSelected) {
        setSelectedTemplate(templates[0].id);
      }
      // If templates are available and selection is valid, do nothing
    } else {
      // If no templates are available
      if (selectedTemplate !== "") {
        setSelectedTemplate("");
      }
    }
  }, [templates, selectedTemplate]); // Rerun when templates or selectedTemplate changes

  // Initialize history with the initial HTML and respond to HTML changes from parent
  useEffect(() => {
    // Ensure initialHtml is treated as a string for safe operations
    const currentSafeInitialHtml = initialHtml || "";

    console.log(
      "initialHtml changed or component mounted:",
      currentSafeInitialHtml.substring(0, 50) + "..."
    );

    // Proceed with logic, using currentSafeInitialHtml
    // The condition `if (initialHtml)` was used before,
    // now we can rely on currentSafeInitialHtml being a string.
    // If initialHtml was truly null and meant "clear content",
    // currentSafeInitialHtml will be "" which is appropriate.

    // Apply keyword highlighting to the HTML if enabled
    const highlightedHtml =
      keywordHighlightEnabled && keywords && keywords.length > 0
        ? highlightKeywordsInHtml(
            currentSafeInitialHtml,
            keywords,
            "keyword-highlight"
          )
        : currentSafeInitialHtml;

    if (initialRenderRef.current) {
      console.log("Initial render - setting up editor with initial HTML");
      setHtml(highlightedHtml);
      setHistory([highlightedHtml]);
      setHistoryIndex(0);
      prevHtmlRef.current = highlightedHtml; // Store the version actually set
      initialRenderRef.current = false;
    } else if (highlightedHtml !== prevHtmlRef.current) {
      // Compare against the previously set highlighted HTML
      // This condition handles external updates to initialHtml
      console.log("External HTML update - updating editor with new HTML");

      // Force a complete refresh of the editor content and history
      setHtml(highlightedHtml);

      // Reset history with this new HTML
      setHistory([highlightedHtml]);
      setHistoryIndex(0);
      prevHtmlRef.current = highlightedHtml;

      // If editor ref exists, update its content directly
      if (editorRef.current) {
        console.log("Directly updating editor DOM with new template HTML");
        editorRef.current.innerHTML = DOMPurify.sanitize(highlightedHtml);
      }
    }
  }, [initialHtml, keywords, keywordHighlightEnabled]);

  // Show temporary operation feedback
  const showFeedback = useCallback((message) => {
    setOperationFeedback({
      visible: true,
      message,
    });

    // Hide feedback after 1.5 seconds
    setTimeout(() => {
      setOperationFeedback({
        visible: false,
        message: "",
      });
    }, 1500);
  }, []);

  // Helper function to check if a node is inside the editor
  const isNodeInEditor = useCallback((node) => {
    if (!editorRef.current || !node) return false;
    return editorRef.current.contains(node) || editorRef.current === node;
  }, []);

  // Function to manually update HTML content
  // This is called when we want to explicitly update the HTML state
  const updateHtmlContent = useCallback(() => {
    if (!editorRef.current) return;

    try {
      const newHtml = editorRef.current.innerHTML;

      // Always update the HTML state and add to history to ensure all changes are tracked
      setHtml(newHtml);
      prevHtmlRef.current = newHtml;
      setIsModified(true);

      // If this is not an undo/redo operation, add to history
      if (!isUndoRedo) {
        // Add the new state to history, removing any future states if we're in the middle of the history
        const newHistory = history.slice(0, historyIndex + 1);
        newHistory.push(newHtml);

        // Limit history size to prevent memory issues (e.g., keep last 50 states)
        if (newHistory.length > 50) {
          newHistory.shift();
        }

        setHistory(newHistory);
        setHistoryIndex(newHistory.length - 1);
      }

      // Reset undo/redo flag
      setIsUndoRedo(false);

      // If there's a callback for content changes, call it
      if (onContentChange) {
        onContentChange({ htmlContent: newHtml });
      }
    } catch (error) {
      console.error("Error updating HTML content:", error);
      setSnackbar({
        open: true,
        message: "Error updating content. Your changes may not be saved.",
        severity: "error",
      });
    }
  }, [history, historyIndex, isUndoRedo, onContentChange]);

  // Create a debounced version of the updateHtmlContent function
  // This is used for non-critical updates (e.g., when leaving the editor)
  const debouncedUpdateHtml = useCallback(debounce(updateHtmlContent, 500), [
    updateHtmlContent,
  ]);

  // Handle content editable changes - track that we're editing
  // and update the HTML state after a short delay
  const handleContentChange = useCallback(() => {
    // Mark that we're in a manual edit
    isManualEditRef.current = true;

    // Update HTML after a short delay to ensure we capture all changes
    // This ensures every edit is captured for undo/redo
    debouncedUpdateHtml();
  }, [debouncedUpdateHtml]);

  // Handle blur event - update HTML when leaving the editor
  const handleBlur = useCallback(() => {
    // Only update if we were in a manual edit
    if (isManualEditRef.current) {
      // Force an immediate update instead of waiting for debounce
      updateHtmlContent();
      isManualEditRef.current = false;
    }
  }, [updateHtmlContent]);

  // Handle undo with improved reliability
  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      try {
        // Set flags to prevent cursor jumps
        isUpdatingContentRef.current = true;
        setIsUndoRedo(true);

        const newIndex = historyIndex - 1;
        const previousHtml = history[newIndex];

        setHistoryIndex(newIndex);
        setHtml(previousHtml);
        prevHtmlRef.current = previousHtml;

        // Update the editor content
        if (editorRef.current) {
          // Save current selection
          const selection = window.getSelection();
          let savedRange = null;
          if (
            selection.rangeCount > 0 &&
            isNodeInEditor(selection.anchorNode)
          ) {
            savedRange = selection.getRangeAt(0).cloneRange();
          }

          // Update content
          editorRef.current.innerHTML = DOMPurify.sanitize(previousHtml);

          // If there's a callback for content changes, call it
          if (onContentChange) {
            onContentChange({ htmlContent: previousHtml });
          }

          // Try to restore selection if possible
          if (
            savedRange &&
            editorRef.current.contains(savedRange.startContainer)
          ) {
            try {
              selection.removeAllRanges();
              selection.addRange(savedRange);
            } catch (e) {
              console.warn("Could not restore selection after undo", e);
            }
          }
        }

        showFeedback("Undo");
      } catch (error) {
        console.error("Error during undo operation:", error);
        setSnackbar({
          open: true,
          message: "Error undoing last change. Please try again.",
          severity: "error",
        });
      } finally {
        // Clear flags
        isUpdatingContentRef.current = false;
      }
    } else {
      // Show feedback that we're at the beginning of history
      showFeedback("Nothing to undo");
    }
  }, [history, historyIndex, onContentChange, showFeedback, isNodeInEditor]);

  // Handle redo with improved reliability
  const handleRedo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      try {
        // Set flags to prevent cursor jumps
        isUpdatingContentRef.current = true;
        setIsUndoRedo(true);

        const newIndex = historyIndex + 1;
        const nextHtml = history[newIndex];

        setHistoryIndex(newIndex);
        setHtml(nextHtml);
        prevHtmlRef.current = nextHtml;

        // Update the editor content
        if (editorRef.current) {
          // Save current selection
          const selection = window.getSelection();
          let savedRange = null;
          if (
            selection.rangeCount > 0 &&
            isNodeInEditor(selection.anchorNode)
          ) {
            savedRange = selection.getRangeAt(0).cloneRange();
          }

          // Update content
          editorRef.current.innerHTML = DOMPurify.sanitize(nextHtml);

          // If there's a callback for content changes, call it
          if (onContentChange) {
            onContentChange({ htmlContent: nextHtml });
          }

          // Try to restore selection if possible
          if (
            savedRange &&
            editorRef.current.contains(savedRange.startContainer)
          ) {
            try {
              selection.removeAllRanges();
              selection.addRange(savedRange);
            } catch (e) {
              console.warn("Could not restore selection after redo", e);
            }
          }
        }

        showFeedback("Redo");
      } catch (error) {
        console.error("Error during redo operation:", error);
        setSnackbar({
          open: true,
          message: "Error redoing last change. Please try again.",
          severity: "error",
        });
      } finally {
        // Clear flags
        isUpdatingContentRef.current = false;
      }
    } else {
      // Show feedback that we're at the end of history
      showFeedback("Nothing to redo");
    }
  }, [history, historyIndex, onContentChange, showFeedback, isNodeInEditor]);

  // Format text with improved reliability and edge case handling
  const formatText = useCallback(
    (formatType) => {
      // Make sure the editor exists
      if (!editorRef.current) {
        console.error("Editor reference is not available");
        setSnackbar({
          open: true,
          message: "Editor is not ready. Please try again.",
          severity: "error",
        });
        return;
      }

      // Get current selection
      const selection = window.getSelection();

      // Only proceed if there's a valid selection inside the editor
      if (selection.rangeCount === 0 || !isNodeInEditor(selection.anchorNode)) {
        setSnackbar({
          open: true,
          message: "Please place cursor in the document or select text first",
          severity: "info",
        });
        return;
      }

      // Save the current selection
      const originalRange = selection.getRangeAt(0).cloneRange();
      let rangeToUse = originalRange;

      // Create a snapshot of the current content for undo functionality
      const currentContent = editorRef.current.innerHTML;

      // Special handling for alignment changes to ensure they're captured in history
      const isAlignmentChange = formatType.startsWith("align");

      // Apply the formatting
      try {
        // Set flag to indicate we're updating content
        isUpdatingContentRef.current = true;

        applyFormatting(formatType);

        // Verify the change was applied by checking if content changed
        const newContent = editorRef.current.innerHTML;
        if (newContent === currentContent && !originalRange.collapsed) {
          console.warn(
            `${formatType} formatting didn't modify content, trying alternative approach`
          );

          // Try alternative approach based on format type
          applyFormattingAlternative(formatType, rangeToUse);
        }

        // Update HTML state immediately after formatting
        updateHtmlContent();

        // Show appropriate feedback based on format type
        switch (formatType) {
          case "alignLeft":
            showFeedback("Aligned left");
            break;
          case "alignCenter":
            showFeedback("Aligned center");
            break;
          case "alignRight":
            showFeedback("Aligned right");
            break;
          case "bold":
            showFeedback("Bold");
            break;
          case "italic":
            showFeedback("Italic");
            break;
          case "heading1":
            showFeedback("Heading 1");
            break;
          case "heading2":
            showFeedback("Heading 2");
            break;
          case "heading3":
            showFeedback("Heading 3");
            break;
          case "bullet":
            showFeedback("Bullet list");
            break;
          default:
            showFeedback("Formatting applied");
        }
      } catch (error) {
        console.error(`Error applying ${formatType} formatting:`, error);
        setSnackbar({
          open: true,
          message: `Error applying formatting. Please try again.`,
          severity: "error",
        });

        // Restore original selection if there was an error
        try {
          selection.removeAllRanges();
          selection.addRange(originalRange);
        } catch (e) {
          console.warn("Could not restore selection after formatting error", e);
        }
      } finally {
        // Clear the updating flag
        isUpdatingContentRef.current = false;
      }
    },
    [updateHtmlContent, showFeedback, isNodeInEditor]
  );

  // Open link popover and save current selection with improved reliability
  const handleLinkClick = useCallback(() => {
    // Save current selection
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      // Only save the selection if it's inside our editor
      if (isNodeInEditor(selection.anchorNode)) {
        // Clone the range to avoid issues if the DOM changes
        setSelectionRange(selection.getRangeAt(0).cloneRange());

        // If no text is selected, show a helpful message
        if (selection.toString().trim() === "") {
          showFeedback("Select text first or enter link text");
        }
      } else {
        // If selection is outside editor, show a message
        showFeedback("Please select text in the document first");
        setSelectionRange(null);
      }
    } else {
      setSelectionRange(null);
    }
  }, [showFeedback, isNodeInEditor]);

  // Insert link with improved reliability
  const insertLink = useCallback(
    (linkText, linkUrl) => {
      if (!linkText || !linkUrl) {
        setSnackbar({
          open: true,
          message: "Please provide both link text and URL",
          severity: "info",
        });
        return;
      }

      // Make sure the editor exists
      if (!editorRef.current) {
        console.error("Editor reference is not available");
        setSnackbar({
          open: true,
          message: "Editor is not ready. Please try again.",
          severity: "error",
        });
        return;
      }

      try {
        // Set flag to indicate we're updating content
        isUpdatingContentRef.current = true;

        // Create a snapshot of the current content for undo functionality
        const currentContent = editorRef.current.innerHTML;

        // Restore selection if we have one
        if (selectionRange) {
          const selection = window.getSelection();
          selection.removeAllRanges();
          selection.addRange(selectionRange);

          // Check if the selection is still valid
          if (selection.rangeCount === 0) {
            throw new Error("Selection is no longer valid");
          }

          // Try standard approach first
          document.execCommand("createLink", false, linkUrl);

          // Verify the change was applied
          const newContent = editorRef.current.innerHTML;
          if (newContent === currentContent) {
            console.warn(
              "Link insertion didn't modify content, trying alternative approach"
            );

            // Alternative approach: create an anchor element and insert it
            const range = selection.getRangeAt(0);
            const anchor = document.createElement("a");
            anchor.href = linkUrl;

            // If there's selected text, use it as link text
            if (range.toString().trim() !== "") {
              anchor.textContent = range.toString();
            } else {
              anchor.textContent = linkText;
            }

            // Clear the selection range and insert our anchor
            range.deleteContents();
            range.insertNode(anchor);
          }
        } else {
          // If no selection, create a new link at cursor position
          const selection = window.getSelection();

          if (
            selection.rangeCount > 0 &&
            isNodeInEditor(selection.anchorNode)
          ) {
            const range = selection.getRangeAt(0);
            const anchor = document.createElement("a");
            anchor.href = linkUrl;
            anchor.textContent = linkText;

            // Insert at cursor position
            range.insertNode(anchor);
          } else {
            // If no valid selection/cursor, append to the end
            const anchor = document.createElement("a");
            anchor.href = linkUrl;
            anchor.textContent = linkText;
            editorRef.current.appendChild(anchor);
          }
        }

        // Update HTML state immediately after inserting link
        updateHtmlContent();

        // Show success message
        showFeedback("Link inserted");
      } catch (error) {
        console.error("Error inserting link:", error);
        setSnackbar({
          open: true,
          message: "Error inserting link. Please try again.",
          severity: "error",
        });
      } finally {
        // Clear the updating flag
        isUpdatingContentRef.current = false;
      }
    },
    [selectionRange, updateHtmlContent, showFeedback, isNodeInEditor]
  );

  // Open color picker and save current selection with improved reliability
  const handleColorClick = useCallback(() => {
    // Save the current selection before opening the color picker
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      // Only save the selection if it's inside our editor
      if (isNodeInEditor(selection.anchorNode)) {
        const range = selection.getRangeAt(0);
        if (range.toString().trim() !== "") {
          setColorSelectionRange(range.cloneRange());
        } else {
          setColorSelectionRange(null);
          // Show a helpful message if no text is selected
          showFeedback("Select text first");
        }
      } else {
        // If selection is outside editor, show a message
        showFeedback("Please select text in the document first");
        setColorSelectionRange(null);
      }
    } else {
      setColorSelectionRange(null);
    }
  }, [showFeedback, isNodeInEditor]);

  // Apply selected color to text with improved reliability
  const handleColorChange = useCallback(
    (color) => {
      // Make sure the editor exists
      if (!editorRef.current) {
        console.error("Editor reference is not available");
        setSnackbar({
          open: true,
          message: "Editor is not ready. Please try again.",
          severity: "error",
        });
        return;
      }

      // Check if we have a saved selection
      if (!colorSelectionRange) {
        setSnackbar({
          open: true,
          message: "Please select text before applying color",
          severity: "info",
        });
        return;
      }

      try {
        // Set flag to indicate we're updating content
        isUpdatingContentRef.current = true;

        // Create a snapshot of the current content for undo functionality
        const currentContent = editorRef.current.innerHTML;

        // Restore the saved selection
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(colorSelectionRange);

        // Check if the selection is still valid
        if (selection.rangeCount === 0 || selection.toString().trim() === "") {
          throw new Error("Selection is no longer valid");
        }

        // Apply color using execCommand
        document.execCommand("styleWithCSS", false, true);
        document.execCommand("foreColor", false, color.hex);

        // Verify the change was applied
        const newContent = editorRef.current.innerHTML;
        if (newContent === currentContent) {
          console.warn(
            "Color change didn't modify content, trying alternative approach"
          );

          // Alternative approach: wrap selection in a span with style
          const range = selection.getRangeAt(0);
          const span = document.createElement("span");
          span.style.color = color.hex;

          try {
            range.surroundContents(span);
          } catch (e) {
            // If surroundContents fails (e.g., with partial selections), use a different approach
            const fragment = range.extractContents();
            span.appendChild(fragment);
            range.insertNode(span);
          }
        }

        // Update HTML state immediately after applying color
        updateHtmlContent();

        // Show feedback
        showFeedback("Color applied");
      } catch (error) {
        console.error("Error applying text color:", error);
        setSnackbar({
          open: true,
          message: "Error applying text color. Please try again.",
          severity: "error",
        });
      } finally {
        // Clear the saved selection
        setColorSelectionRange(null);

        // Clear the updating flag
        isUpdatingContentRef.current = false;
      }
    },
    [colorSelectionRange, updateHtmlContent, showFeedback]
  );

  // Handle template change with improved feedback
  const handleTemplateChange = useCallback(
    (template) => {
      if (template === selectedTemplate) return;

      console.log(
        `Template change requested: ${selectedTemplate} -> ${template}`
      );

      // Update the selected template state
      setSelectedTemplate(template);
      showFeedback(`Template: ${template}`);

      // Notify parent component about the template change
      if (onContentChange) {
        console.log("Calling parent onContentChange with template:", template);
        onContentChange({
          htmlContent: html,
          template,
        });
      }
    },
    [html, selectedTemplate, onContentChange, showFeedback]
  );

  // Handle Word document download with improved error handling
  const handleDownloadWord = useCallback(async () => {
    // Update HTML state before downloading
    updateHtmlContent();

    setDownloading(true);
    showFeedback("Preparing document...");

    try {
      if (onDownload) {
        // Create a clean version of the HTML without keyword highlights for export
        let htmlToExport = html;
        if (keywordHighlightEnabled) {
          // Remove the highlight spans, keeping the inner text
          htmlToExport = html.replace(
            /<span class="keyword-highlight">(.*?)<\/span>/gi,
            "$1"
          );
          // Optional: Add a log to confirm stripping
          console.log("Stripped keyword highlights for DOCX export.");
        }

        await onDownload({
          // Pass the cleaned HTML to the download handler
          html: htmlToExport, // Use the cleaned version
          template: selectedTemplate,
          resumeData,
        });
      }

      // Show success message
      setSnackbar({
        open: true,
        message: "Resume downloaded successfully!",
        severity: "success",
      });

      // Reset modified flag after successful download
      setIsModified(false);
    } catch (error) {
      console.error("Error downloading document:", error);

      // Show error message
      setSnackbar({
        open: true,
        message: "Error downloading resume. Please try again.",
        severity: "error",
      });
    } finally {
      setDownloading(false);
    }
  }, [
    html,
    selectedTemplate,
    resumeData,
    onDownload,
    showFeedback,
    updateHtmlContent,
  ]);

  // Close snackbar
  const handleCloseSnackbar = useCallback(() => {
    setSnackbar({ ...snackbar, open: false });
  }, [snackbar]);

  // Handle click inside the editor to preserve cursor position
  const handleEditorClick = useCallback((event) => {
    // Do nothing special, just let the browser handle the click normally
    // This prevents any focus/selection manipulation that might cause cursor jumping
  }, []);

  // Function to get current resume data as JSON for AI editing
  const getCurrentResumeJsonForAI = useCallback(async () => {
    if (!resumeData) {
      console.error(
        "Resume data is not available in EnhancedHtmlEditor. Cannot proceed with AI edit."
      );
      setSnackbar({
        open: true,
        message:
          "Cannot edit with AI: Source resume data is missing. Please try reloading.",
        severity: "error",
      });
      throw new Error("Source resume data is missing.");
    }
    // Assuming resumeData is already in or can be directly used as ResumeSchema
    // Return a deep copy to prevent unintended mutations if resumeData is used elsewhere
    return JSON.parse(JSON.stringify(resumeData));
  }, [resumeData]);

  // Function to update the editor content from AI modified JSON
  const handleUpdateResumeFromJson = useCallback(
    (editedResumeJson) => {
      const currentTemplateObject = templates.find(
        (t) => t.id === selectedTemplate
      );
      if (
        currentTemplateObject &&
        typeof currentTemplateObject.generator === "function"
      ) {
        try {
          isUpdatingContentRef.current = true; // Prevent cursor jumps during update
          const newRawHtml = currentTemplateObject.generator(editedResumeJson);
          const newSanitizedHtml = DOMPurify.sanitize(newRawHtml);

          // Apply keyword highlighting if enabled
          const finalHtml =
            keywordHighlightEnabled && keywords && keywords.length > 0
              ? highlightKeywordsInHtml(
                  newSanitizedHtml,
                  keywords,
                  "keyword-highlight"
                )
              : newSanitizedHtml;

          setHtml(finalHtml); // Update internal HTML state
          setHistory([finalHtml]); // Reset history with the new state
          setHistoryIndex(0);
          prevHtmlRef.current = finalHtml; // Update previous HTML ref

          if (editorRef.current) {
            editorRef.current.innerHTML = finalHtml; // Update editor DOM directly
          }

          // Notify parent component about the change, providing new HTML, template, and updated data
          if (onContentChange) {
            onContentChange({
              htmlContent: finalHtml,
              template: selectedTemplate,
              updatedResumeData: editedResumeJson, // Pass the new data source
            });
          }
          showFeedback("Resume updated by AI!");
        } catch (error) {
          console.error(
            "Error generating HTML from AI-updated resume data:",
            error
          );
          setSnackbar({
            open: true,
            message: "Error applying AI changes to editor.",
            severity: "error",
          });
        } finally {
          isUpdatingContentRef.current = false;
        }
      } else {
        console.error(
          "Cannot update editor: Template generator not found or not a function for template:",
          selectedTemplate
        );
        setSnackbar({
          open: true,
          message:
            "Error: Template information missing for AI update. Cannot regenerate HTML.",
          severity: "error",
        });
        // Fallback: notify parent with the raw data if HTML generation fails
        if (onContentChange) {
          onContentChange({
            updatedResumeData: editedResumeJson,
            template: selectedTemplate,
            htmlContent: null, // Indicate HTML needs regeneration by parent
          });
        }
      }
    },
    [
      templates,
      selectedTemplate,
      onContentChange,
      showFeedback,
      keywords,
      keywordHighlightEnabled,
    ]
  );

  // Memoize the document page to prevent unnecessary re-renders
  const documentPage = useMemo(
    () => (
      <DocumentPage
        content={html}
        isEditable={true}
        isHtml={true} // Indicate that content is HTML, not Markdown
        onInput={handleContentChange}
        onBlur={handleBlur}
        onClick={handleEditorClick}
        editorRef={editorRef}
        showPageBreaks={false} // Disable page breaks during editing to prevent layout shifts
        pageHeight={1056} // Standard US letter height (11in * 96dpi)
      />
    ),
    [html, handleContentChange, handleBlur, handleEditorClick]
  );

  // Debug templates
  useEffect(() => {
    console.log("Templates in EnhancedHtmlEditor:", templates);
  }, [templates]);

  // Get current scale from element
  const getCurrentScale = () => {
    if (!editorRef.current) return 1;

    const transform = editorRef.current.style.transform;
    if (!transform || !transform.includes("scale")) return 1;

    const match = transform.match(/scale\(([^)]+)\)/);
    return match ? parseFloat(match[1]) : 1;
  };

  return (
    <Box className={classes.root}>
      {/* Template selector */}
      <TemplateSelector
        selectedTemplate={selectedTemplate}
        onTemplateChange={handleTemplateChange}
        templates={templates}
      />

      {/* Document editing toolbar */}
      <EditorToolbar
        onFormatText={formatText}
        onUndo={handleUndo}
        onRedo={handleRedo}
        canUndo={historyIndex > 0}
        canRedo={historyIndex < history.length - 1}
        onInsertLink={insertLink}
        onLinkClick={handleLinkClick}
        onColorClick={handleColorClick}
        onColorChange={handleColorChange}
        onDownload={handleDownloadWord}
        downloading={downloading}
        hasContent={!!html.trim()}
        // ATS Analysis button (only shown for resume)
        onScrollToATS={onScrollToATS}
        // AI Edit props - pass documentType and use generic prop names
        documentType={documentType}
        getCurrentDocumentJson={getCurrentResumeJsonForAI} // Still using the resume-specific internal function name here
        onUpdateDocumentFromJson={handleUpdateResumeFromJson} // Still using the resume-specific internal function name here
        // Mobile-specific props
        onMobileZoomIn={() => {
          if (editorRef.current) {
            const currentScale = getCurrentScale();
            editorRef.current.style.transform = `scale(${Math.min(
              currentScale + 0.1,
              1.5
            )})`;
          }
        }}
        onMobileZoomOut={() => {
          if (editorRef.current) {
            const currentScale = getCurrentScale();
            editorRef.current.style.transform = `scale(${Math.max(
              currentScale - 0.1,
              0.3
            )})`;
          }
        }}
        onMobileResetZoom={() => {
          if (editorRef.current) {
            // Calculate optimal zoom level for current screen
            const optimalScale =
              window.innerWidth < 600
                ? Math.max((window.innerWidth - 24) / 816, 0.35) * 0.85
                : 1;
            editorRef.current.style.transform = `scale(${optimalScale})`;
          }
        }}
        currentScale={getCurrentScale()}
        showMobileControls={window.innerWidth < 600}
        onToggleKeywordHighlight={() => {
          const newState = !keywordHighlightEnabled;
          setKeywordHighlightEnabled(newState);

          // Re-apply or remove highlighting based on the new state
          if (editorRef.current) {
            try {
              const currentHtml = editorRef.current.innerHTML;
              let processedHtml;

              if (newState && keywords && keywords.length > 0) {
                // Apply highlighting
                processedHtml = highlightKeywordsInHtml(
                  currentHtml,
                  keywords,
                  "keyword-highlight"
                );
                showFeedback("Keywords highlighted");
              } else {
                // Remove highlighting
                processedHtml = currentHtml.replace(
                  /<span class="keyword-highlight">(.*?)<\/span>/gi,
                  "$1"
                );
                showFeedback("Keywords hidden");
              }

              // Update the editor content and state
              editorRef.current.innerHTML = DOMPurify.sanitize(processedHtml);

              // Add to history and update state
              const newHistory = history.slice(0, historyIndex + 1);
              newHistory.push(processedHtml);
              setHistory(newHistory);
              setHistoryIndex(newHistory.length - 1);
              setHtml(processedHtml);

              // Update parent component if needed
              if (onContentChange) {
                onContentChange({ htmlContent: processedHtml });
              }
            } catch (error) {
              console.error("Error toggling keyword highlighting:", error);
              setSnackbar({
                open: true,
                message: "Error toggling keyword highlighting",
                severity: "error",
              });
            }
          }
        }}
        keywordHighlightEnabled={keywordHighlightEnabled}
      />

      {/* WYSIWYG editor styled like a Word document with pagination */}
      <Box className={classes.pagesContainer} ref={containerRef}>
        {/* Operation feedback indicator */}
        <div
          className={`${classes.feedbackIndicator} ${
            operationFeedback.visible ? classes.feedbackVisible : ""
          }`}
        >
          {operationFeedback.message}
        </div>

        {/* Single editable document with visual page breaks */}
        {documentPage}
      </Box>

      {/* Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EnhancedHtmlEditor;
